import React, { useEffect, useState, useCallback } from "react";
import { FiRefreshCw, FiAlertTriangle, FiCheckCircle } from "react-icons/fi";
import { getApiCall } from "../../service/httpCall";
import { API_CALL_CENTRE_ADMIN_ALERT } from "../../service/url";
import { USER_ROLES } from "../../utilities/constant";
import { Storage } from "../../utilities/setting";
import { toast } from "react-toastify";

const CallCenterAdminAlert = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCallCenterAdmin, setIsCallCenterAdmin] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  useEffect(() => {
    try {
      const rawUser = Storage.getItem("user");
      if (!rawUser) return;
      const user = JSON.parse(rawUser);
      if (user?.role === USER_ROLES.CALL_CENTRE_ADMIN) {
        setIsCallCenterAdmin(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const fetchAlert = useCallback((showToast = false) => {
    setLoading(true);
    getApiCall(API_CALL_CENTRE_ADMIN_ALERT)
      .then((resp) => {
        setData(resp);
        setLastRefreshedAt(new Date());
        if (showToast) toast.success("Alerts refreshed");
      })
      .catch((err) => {
        if (showToast) toast.error(err || "Failed to refresh alerts");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isCallCenterAdmin) return;
    fetchAlert(false);
    // const interval = setInterval(() => fetchAlert(false), 60_000);
    // return () => clearInterval(interval);
  }, [isCallCenterAdmin, fetchAlert]);

  if (!isCallCenterAdmin) return null;
  if (!data && !loading) return null;

  const hasAlerts = !!data?.has_alerts;
  const channelLicence = data?.channel_licence || {};
  const parentCompanies = data?.parent_companies || [];

  const palette = hasAlerts
    ? {
        bg: "linear-gradient(135deg, #800017 0%, #794100 100%)",
        border: "#ffb703",
        text: "#fff",
        pillBg: "rgba(0,0,0,0.18)",
        pillBorder: "rgba(255,255,255,0.4)",
        badBg: "#ffd60a",
        badText: "#1a1a1a",
      }
    : {
        bg: "linear-gradient(135deg, #004e34 0%, #004e35 100%)",
        border: "#34d399",
        text: "#fff",
        pillBg: "rgba(0,0,0,0.15)",
        pillBorder: "rgba(255,255,255,0.35)",
        badBg: "#fff",
        badText: "#065f46",
      };

  const formatBalance = (n) =>
    Number.isFinite(Number(n))
      ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })
      : "0";

  const lastRefreshedLabel = lastRefreshedAt
    ? lastRefreshedAt.toLocaleTimeString()
    : "—";

  return (
    <div
      style={{
        marginBottom: 28,
        borderRadius: 16,
        padding: 18,
        // background: palette.bg,
        border: `2px solid ${palette.border}`,
        // color: palette.text,
        boxShadow: hasAlerts
          ? "0 0 0 0 rgba(255,45,85,0.45), 0 10px 30px rgba(255,45,85,0.35)"
          : "0 6px 20px rgba(16,185,129,0.25)",
        animation: hasAlerts
          ? "ccAdminAlertPulse 1.6s ease-in-out infinite"
          : "none",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes ccAdminAlertPulse {
          0% { box-shadow: 0 0 0 0 rgba(255,45,85,0.55), 0 10px 30px rgba(255,45,85,0.35); }
          70% { box-shadow: 0 0 0 14px rgba(255,45,85,0), 0 10px 30px rgba(255,45,85,0.25); }
          100% { box-shadow: 0 0 0 0 rgba(255,45,85,0), 0 10px 30px rgba(255,45,85,0.35); }
        }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {hasAlerts ? (
            <FiAlertTriangle size={22} />
          ) : (
            <FiCheckCircle size={22} />
          )}
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: 0.3 }}>
              {hasAlerts ? "Action Required" : "All Clear"}
            </div>
            <div style={{ fontSize: 12 }}>
              Call Center: <strong>{data?.call_center_name || "—"}</strong>
              {"  •  "}Last checked: {lastRefreshedLabel}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => fetchAlert(true)}
          disabled={loading}
          style={{
            border: `1px solid ${palette.pillBorder}`,
            // background: palette.pillBg,
            // color: palette.text,
            borderRadius: 999,
            height: 34,
            padding: "0 14px",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
          title="Reload Alerts"
        >
          <FiRefreshCw
            size={14}
            style={{
              animation: loading ? "spin 0.9s linear infinite" : "none",
            }}
          />
          {loading ? "Refreshing…" : "Reload"}
          <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
        </button>
      </div>

      {Array.isArray(data?.alerts) && data.alerts.length > 0 && (
        <ul
          style={{
            marginTop: 12,
            marginBottom: 4,
            paddingLeft: 22,
            fontSize: 13.5,
            lineHeight: 1.45,
          }}
        >
          {data.alerts.map((msg, idx) => (
            <li key={idx} style={{ fontWeight: 600 }}>
              {msg}
            </li>
          ))}
        </ul>
      )}

      <div
        style={{
          marginTop: 14,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}
      >
        <div
          style={{
            // background: palette.pillBg,
            border: `1px solid ${palette.pillBorder}`,
            borderRadius: 12,
            padding: "10px 12px",
          }}
        >
          <div
            style={{
              fontSize: 11,
              // opacity: 0.9,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            CHANNEL LICENCE
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              marginTop: 4,
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800 }}>
              {channelLicence.total ?? 0}
            </div>
            <div style={{ fontSize: 12 }}>
              total · {channelLicence.available ?? 0} avail ·{" "}
              {channelLicence.used ?? 0} used
            </div>
          </div>
          <div
            style={{
              marginTop: 6,
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 999,
              // background: channelLicence.valid
              //   ? "rgba(255,255,255,0.18)"
              //   : palette.badBg,
              // color: channelLicence.valid ? palette.text : palette.badText,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.4,
            }}
          >
            {channelLicence.valid ? "VALID" : "INVALID (=0)"}
          </div>
        </div>

        {parentCompanies.map((company) => (
          <div
            key={company.id}
            style={{
              // background: palette.pillBg,
              border: `1px solid ${palette.pillBorder}`,
              borderRadius: 12,
              padding: "10px 12px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                // opacity: 0.9,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={company.name}
            >
              {company.name}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                marginTop: 4,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800 }}>
                {formatBalance(company.voice_balance)}
              </div>
              <div style={{ fontSize: 12 }}>voice balance</div>
            </div>
            <div
              style={{
                marginTop: 6,
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: 999,
                // background: company.voice_balance_valid
                //   ? "rgba(255,255,255,0.18)"
                //   : palette.badBg,
                // color: company.voice_balance_valid
                //   ? palette.text
                //   : palette.badText,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.4,
              }}
            >
              {company.voice_balance_valid ? "OK" : "EMPTY"}
            </div>
          </div>
        ))}

        {parentCompanies.length === 0 && (
          <div
            style={{
              // background: palette.pillBg,
              border: `1px solid ${palette.pillBorder}`,
              borderRadius: 12,
              padding: "10px 12px",
              fontSize: 12.5,
              // opacity: 0.95,
            }}
          >
            No parent companies found.
          </div>
        )}
      </div>
    </div>
  );
};

export default CallCenterAdminAlert;
